/**
 * THE CONFIRMATION LAYER.
 *
 * Every roof-detection experiment this product has run failed in the same
 * place, and it was never detection. Sol V1 and V2 both bridged a run across
 * vegetation. Sol V2 ran 209 px past a front corner onto a side elevation;
 * Gemini did both, and ran 226 px. In each case the geometry was plausible
 * enough to look right and wrong enough to put footage nobody was going to
 * install onto a customer's invoice.
 *
 * So the guard is not a better detector. It is this: geometry that no person
 * has agreed to is not in the priced collection at all. buildQuote and
 * buildMaterials walk doc.layers; proposals live in doc.proposals; a proposal
 * earns a Strand by being confirmed and loses it the instant it is edited.
 *
 * Everything in this file is a pure function of a document. Nothing here
 * mutates its argument, nothing reads a clock it was not handed, and nothing
 * touches the store. That is what lets the store do a confirmation, the
 * invalidation it implies, and the undo snapshot inside a single set(), which
 * is the only way requirement 6 can hold: there is never a frame in which an
 * edited proposal and its stale priced Strand both exist.
 */

import type { Layer, LightsOsDocument, Point, Strand } from '../types/document'
import { normalizePaper } from './paper'
import { defaultPricing, defaultRates } from '../types/pricing'
import { SCHEMA_VERSION, isStrand } from '../types/document'
import type {
  CorrectionCounts,
  EndState,
  ProposalOrigin,
  ProposalSpan,
  RoofProposal,
} from '../types/proposal'
import { emptyCorrectionLog } from '../types/proposal'

/**
 * WHICH STRAND KINDS GO THROUGH CONFIRMATION.
 *
 * Roof runs only, and deliberately so. The failures this layer exists to stop
 * are all roof-edge failures: a line in the wrong lane, a line across a tree,
 * a line around a front corner. A bush drawn by hand with a finger has no
 * detector to be wrong about it and no hidden footage to argue over.
 *
 * One constant, so widening it later is one edit rather than an audit.
 */
export const PROPOSAL_KINDS: ReadonlySet<Strand['kind']> = new Set(['roofline'])

export const needsConfirmation = (kind: Strand['kind']) => PROPOSAL_KINDS.has(kind)

/** The one kind of layer a confirmed proposal is ever allowed to name. */
const isConfirmableStrand = (l: Layer | undefined): l is Strand =>
  !!l && isStrand(l) && needsConfirmation(l.kind)

/* ------------------------------------------------------- canonical geometry */

/**
 * The exact text of a run's geometry and topology.
 *
 * FULL PRECISION, on purpose. An earlier version rounded to six decimals,
 * which on a 4000 px photograph is four thousandths of a pixel - small, and
 * not zero, and "small" is not a thing a quote boundary is allowed to decide.
 * String(n) round-trips a double exactly, so two runs produce the same text
 * only if every coordinate is the same number.
 *
 * What is in here is what invalidates a confirmation. What is left out stays
 * editable on a confirmed run: color, palette, name, product, brightness and
 * bulb scale, because recoloring a roofline changes what it looks like and
 * not one inch of what it costs. decidedAt is left out too - a timestamp is a
 * record of a decision, not the decision, so re-saving withdraws nothing.
 */
export function canonicalGeometry(p: RoofProposal): string {
  const pts = p.points.map((q) => `${String(q.x)},${String(q.y)}`).join(';')
  const end = (e: EndState) =>
    [
      e.kind,
      e.open ? '1' : '0',
      e.weldedTo ?? '',
      e.sideReturnWarning ? '1' : '0',
      e.sideReturnDecision ?? '',
      e.trimToIndex ?? '',
      e.trimApplied ? '1' : '0',
    ].join('|')
  const spans = p.spans
    .map((s) => [s.fromIndex, s.toIndex, s.visibility, s.obstruction ?? '', s.decision ?? ''].join('|'))
    .join(';')
  return ['v2', p.kind, pts, end(p.ends.start), end(p.ends.end), spans].join('~')
}

/** FNV-1a. No dependency, deterministic everywhere we ship. */
function fnv(s: string, seed: number): number {
  let h = seed >>> 0
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    h = Math.imul(h ^ (c & 0xff), 0x01000193) >>> 0
    h = Math.imul(h ^ (c >>> 8), 0x01000193) >>> 0
  }
  return h >>> 0
}

const hex8 = (n: number) => n.toString(16).padStart(8, '0')

/**
 * A SHORT NAME FOR A GEOMETRY, AND NOTHING MORE.
 *
 * Useful in a log, a correction record or a diff. It is deliberately NOT what
 * a pricing decision rests on: the authority is geometryRev plus the exact
 * canonical text above, both of which this cannot be substituted for.
 */
export function geometryFingerprint(p: RoofProposal): string {
  const s = canonicalGeometry(p)
  return `${hex8(fnv(s, 0x811c9dc5))}${hex8(fnv(s, 0x9e3779b9))}:${s.length.toString(36)}`
}

/** Exact, not approximate. Two points are the same point or they are not. */
const samePoints = (a: Point[], b: Point[]) =>
  a.length === b.length && a.every((p, i) => p.x === b[i].x && p.y === b[i].y)

/* -------------------------------------------------------------- construction */

export const defaultEnd = (kind: EndState['kind'] = 'endpoint'): EndState => ({
  kind,
  open: true,
  sideReturnWarning: false,
})

export const clearSpans = (pointCount: number): ProposalSpan[] =>
  Array.from({ length: Math.max(0, pointCount - 1) }, (_, i) => ({
    fromIndex: i,
    toIndex: i + 1,
    visibility: 'clear' as const,
  }))

export function makeProposal(args: {
  id: string
  points: Point[]
  origin: ProposalOrigin
  createdAt: string
  kind?: RoofProposal['kind']
  ends?: Partial<RoofProposal['ends']>
  spans?: ProposalSpan[]
  provider?: string
  promptVersion?: string
  schemaHash?: string
}): RoofProposal {
  return {
    id: args.id,
    origin: args.origin,
    provider: args.provider,
    promptVersion: args.promptVersion,
    schemaHash: args.schemaHash,
    createdAt: args.createdAt,
    points: args.points.map((p) => ({ x: p.x, y: p.y })),
    kind: args.kind ?? 'eave',
    ends: {
      start: args.ends?.start ?? defaultEnd(),
      end: args.ends?.end ?? defaultEnd(),
    },
    spans: args.spans ?? clearSpans(args.points.length),
    geometryRev: 0,
    status: 'proposed',
  }
}

/* ------------------------------------------------------------------ confirm */

export type BlockerCode =
  | 'too_few_points'
  | 'span_shape'
  | 'side_return_undecided'
  | 'side_return_trim_target_missing'
  | 'trim_leaves_nothing'
  | 'occlusion_undecided'
  | 'occlusion_exclude_needs_split'
  | 'already_rejected'

export interface Blocker {
  code: BlockerCode
  /** Plain English, shown on the run card. */
  message: string
  /** Which end or span the person has to answer for. */
  at?: 'start' | 'end' | number
}

/**
 * WHY A RUN CANNOT BE CONFIRMED YET.
 *
 * A warning that can be dismissed is not a decision. Both of the questions
 * this layer asks - does the run carry on past the front corner, does the edge
 * carry on behind the tree - have an answer that quotes footage and an answer
 * that does not, and neither is the default. Until one is chosen the run stays
 * proposed and contributes nothing.
 */
export function canConfirm(p: RoofProposal): { ok: boolean; blockers: Blocker[] } {
  const blockers: Blocker[] = []

  if (p.status === 'rejected') {
    blockers.push({ code: 'already_rejected', message: 'This run was removed.' })
  }
  if (p.points.length < 2) {
    blockers.push({ code: 'too_few_points', message: 'A run needs at least two points.' })
  }
  if (p.spans.length !== Math.max(0, p.points.length - 1)) {
    blockers.push({
      code: 'span_shape',
      message: 'This run is malformed: its visibility spans do not match its points.',
    })
  }

  for (const which of ['start', 'end'] as const) {
    const e = p.ends[which]
    if (!e.sideReturnWarning) continue
    if (e.sideReturnDecision !== 'trim' && e.sideReturnDecision !== 'include') {
      blockers.push({
        code: 'side_return_undecided',
        at: which,
        message:
          'This end looks like it carries on past the front corner onto the side of the house. Say whether to trim it or light it.',
      })
    } else if (e.sideReturnDecision === 'trim' && e.trimToIndex == null && !e.trimApplied) {
      blockers.push({
        code: 'side_return_trim_target_missing',
        at: which,
        message: 'Trim was chosen but there is no front corner recorded to trim back to.',
      })
    }
  }

  p.spans.forEach((s, i) => {
    if (s.visibility === 'clear') return
    if (s.decision === 'continues') return
    if (s.decision === 'exclude') {
      blockers.push({
        code: 'occlusion_exclude_needs_split',
        at: i,
        message:
          'Split this run at the hidden stretch before confirming, so the excluded gap is not inside a single priced run.',
      })
      return
    }
    /*
     * A PALM IN FRONT OF A ROOF IS NOT A QUESTION.
     *
     * This used to stop on every occluded span, which meant a contractor was
     * interrogated about each tree on the property before he could price a
     * roofline he could see perfectly well continuing on both sides of it. On
     * a South Florida house that is most of the runs, and a question asked
     * that often stops being read.
     *
     * So foliage in front of a visible edge is just foliage: the run stays one
     * straight line and can be shortened or split if it turns out not to be.
     * The question is kept for the case it was written for - a stretch where
     * nothing can tell whether the edge carries on at all, which a source
     * marks as cannot_determine rather than merely occluded.
     */
    if (s.visibility !== 'cannot_determine') return
    blockers.push({
      code: 'occlusion_undecided',
      at: i,
      message:
        'Nothing in the photo shows whether the edge carries on through here. Say whether it continues or whether that stretch is a gap.',
    })
  })

  if (blockers.some((b) => b.code === 'side_return_undecided' || b.code === 'side_return_trim_target_missing')) {
    return { ok: false, blockers }
  }
  const trimmed = applyTrims(p)
  if (trimmed.points.length < 2) {
    blockers.push({
      code: 'trim_leaves_nothing',
      message: 'Trimming at the front corner would leave nothing to light.',
    })
  }
  return { ok: blockers.length === 0, blockers }
}

const clampIdx = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(n)))

/**
 * "Trim at front corner" is an edit, not a note.
 *
 * The thing that raised the warning knows where the corner is, so the person
 * gets to choose the outcome rather than then having to drag a handle to the
 * pixel. The trimmed geometry is what gets confirmed and what gets priced, so
 * the confirmation records it and the picture cannot disagree with the invoice.
 *
 * trimApplied survives the cut. Without it the question could never be
 * answered twice: confirming consumed trimToIndex, and any later edit withdrew
 * the confirmation and then refused to give it back, because the corner it
 * wanted to trim to was gone.
 */
export function applyTrims(p: RoofProposal): RoofProposal {
  const last = p.points.length - 1
  let lo = 0
  let hi = last
  const s = p.ends.start
  const e = p.ends.end
  if (s.sideReturnDecision === 'trim' && s.trimToIndex != null) lo = clampIdx(s.trimToIndex, 0, last)
  if (e.sideReturnDecision === 'trim' && e.trimToIndex != null) hi = clampIdx(e.trimToIndex, lo, last)
  if (lo === 0 && hi === last) return p
  const points = p.points.slice(lo, hi + 1)
  const spans = p.spans.slice(lo, hi).map((sp, i) => ({ ...sp, fromIndex: i, toIndex: i + 1 }))
  return {
    ...p,
    points,
    spans,
    // A cut is an edit like any other, so it moves the revision on.
    geometryRev: p.geometryRev + 1,
    ends: {
      start: { ...s, trimToIndex: undefined, trimApplied: lo > 0 ? true : s.trimApplied },
      end: { ...e, trimToIndex: undefined, trimApplied: hi < last ? true : e.trimApplied },
    },
  }
}

/** Everything a Strand needs that a proposal does not carry. */
export type StrandTemplate = Omit<Strand, 'id' | 'type' | 'kind' | 'name' | 'visible' | 'points'>

/**
 * THE SETTINGS A RUN IS ALREADY WEARING.
 *
 * A run that is relit after an edit has to come back looking like itself. Pull
 * the template off the Strand it owned rather than off the current defaults,
 * or dragging a red run would hand it back warm white - the same class of bug
 * confirmRun had before it started passing a template.
 *
 * Geometry, identity and name are deliberately not in here: those come from
 * the proposal and from the caller.
 */
export function strandTemplate(l: Strand): StrandTemplate {
  const { id: _id, type: _type, kind: _kind, name: _name, visible: _visible, points: _points, ...rest } = l
  return rest
}

export const baseStrandTemplate = (): StrandTemplate => ({
  product: 'C9',
  palette: ['#FFB55A'],
  patternRepeat: 1,
  bulbScale: 1,
  spacingScale: 1,
  brightness: 1,
  sag: 0,
  depthStart: 1,
  depthEnd: 0.82,
  wrapWidth: 0.028,
  wrapTurns: 14,
  wrapCap: false,
  capPalette: ['#E02B2B'],
  capLength: 0.05,
  capFronds: 9,
  capDroop: 0.55,
})

export interface ConfirmResult {
  doc: LightsOsDocument
  ok: boolean
  blockers: Blocker[]
  strandId?: string
  /** True when the proposal was already confirmed and nothing was created. */
  noop?: boolean
}

/**
 * Confirm one proposal. Exactly one Strand, or none.
 *
 * Confirming a run that is already confirmed and still valid is a no-op rather
 * than a second Strand. Double taps happen, React renders twice in
 * development, and a duplicated roofline is a duplicated invoice.
 */
export function confirmProposal(
  doc: LightsOsDocument,
  proposalId: string,
  opts: { now: string; strandId: string; template?: StrandTemplate; name?: string },
): ConfirmResult {
  const idx = (doc.proposals ?? []).findIndex((p) => p.id === proposalId)
  if (idx < 0) {
    return {
      doc,
      ok: false,
      blockers: [{ code: 'already_rejected', message: 'That run no longer exists.' }],
    }
  }
  const p = doc.proposals[idx]

  if (p.status === 'confirmed' && p.strandId && confirmationHolds(doc, p)) {
    return { doc, ok: true, blockers: [], strandId: p.strandId, noop: true }
  }

  const check = canConfirm(p)
  if (!check.ok) return { doc, ok: false, blockers: check.blockers }

  const trimmed = applyTrims(p)
  const strand: Strand = {
    ...(opts.template ?? baseStrandTemplate()),
    id: opts.strandId,
    type: 'strand',
    // The one kind a confirmation may produce, so the check on the way back
    // out cannot be satisfied by anything else.
    kind: 'roofline',
    name: opts.name ?? 'Roofline',
    visible: true,
    // Written from schema 4 on: this line was agreed to by a person.
    origin: 'confirmed',
    points: trimmed.points.map((q) => ({ x: q.x, y: q.y })),
  }

  const confirmed: RoofProposal = {
    ...trimmed,
    status: 'confirmed',
    confirmedAt: opts.now,
    strandId: strand.id,
    confirmedRev: trimmed.geometryRev,
    confirmedGeometry: canonicalGeometry(trimmed),
    confirmedFingerprint: geometryFingerprint(trimmed),
  }

  // Drop any Strand this proposal used to own before adding the new one, so a
  // stale confirmation can never leave two priced runs behind.
  const layers = doc.layers.filter((l) => l.id !== p.strandId).concat(strand)
  const proposals = doc.proposals.map((q, i) => (i === idx ? confirmed : q))

  return {
    // Reconciled on the way out: it proves the new confirmation holds under
    // the same rules everything else is judged by, and it is what ends a
    // migration review once the last legacy run has been dealt with.
    doc: reconcile(bump(doc, { proposals, layers }, { runsConfirmedUnchanged: 1 })).doc,
    ok: true,
    blockers: [],
    strandId: strand.id,
  }
}

/* --------------------------------------------------------------- withdrawal */

/**
 * EVERY CONDITION A CONFIRMATION HAS TO SATISFY, IN ONE PLACE.
 *
 * Read it as a list of ways a quote could be wrong:
 *   the run is not confirmed                    - nobody said yes
 *   it names no Strand                          - there is nothing to price
 *   the named Strand is absent                  - a dangling reference
 *   the named layer is a decoration             - the wrong kind of thing
 *   the named Strand is a bush or a wrap        - the wrong kind of run
 *   the revision moved                          - it was edited
 *   the canonical text differs                  - it is not the same line
 *   the Strand's points differ                  - the picture and the run disagree
 *   the spans do not match the points           - it is malformed
 * Any one of them fails closed.
 */
export function confirmationHolds(doc: LightsOsDocument, p: RoofProposal): boolean {
  if (p.status !== 'confirmed' || !p.strandId) return false
  if (p.spans.length !== Math.max(0, p.points.length - 1)) return false
  if (p.confirmedRev == null || p.confirmedRev !== p.geometryRev) return false
  if (!p.confirmedGeometry || p.confirmedGeometry !== canonicalGeometry(p)) return false
  const layer = doc.layers.find((l) => l.id === p.strandId)
  if (!isConfirmableStrand(layer)) return false
  if (!samePoints(layer.points, p.points)) return false
  // Two proposals claiming one Strand: neither claim is trustworthy.
  const claims = (doc.proposals ?? []).filter((q) => q.strandId === p.strandId).length
  return claims === 1
}

/**
 * THE SWEEP THAT ENFORCES REQUIREMENT 1.
 *
 * Any editing action on a confirmed run withdraws the confirmation and deletes
 * the priced Strand, in the same returned document. There is no threshold, one
 * bulb or otherwise: a run that has been edited is a run nobody has agreed to,
 * and the size of the change is not the point.
 *
 * Run this after every mutation of a proposal, inside the same set(), so a
 * withdrawn confirmation and its dead Strand never coexist in a rendered frame
 * or in a saved file.
 */
export function reconcile(doc: LightsOsDocument): { doc: LightsOsDocument; withdrawn: number } {
  const proposals = doc.proposals ?? []
  const drop = new Set<string>()
  let withdrawn = 0

  const next = proposals.map((p) => {
    if (p.status === 'confirmed' && confirmationHolds(doc, p)) return p
    if (p.status === 'confirmed') withdrawn++
    if (!p.strandId) return p.status === 'confirmed' ? { ...p, status: 'proposed' as const } : p
    drop.add(p.strandId)
    return {
      ...p,
      status: p.status === 'confirmed' ? ('proposed' as const) : p.status,
      strandId: undefined,
      confirmedAt: undefined,
      confirmedRev: undefined,
      confirmedGeometry: undefined,
      confirmedFingerprint: undefined,
    }
  })

  /*
   * A ROOF STRAND NOBODY OWNS IS ADOPTED, NOT LEFT LYING THERE.
   *
   * It cannot arrive through any legal path, so the file was hand-edited or a
   * migration was skipped. Refusing to price it is necessary and not
   * sufficient: left in the priced collection it still gets DRAWN, so the
   * customer sees lights on a roof that the quote does not charge for, and the
   * picture and the price disagree - which is the one thing this document
   * format exists to prevent.
   *
   * So it comes out of layers and back in as a proposal, with its provenance
   * recorded as unknown. Nothing is lost, nothing is priced, and the run turns
   * into a dashed line with a Confirm button on it.
   */
  const claimedNow = new Set(next.map((p) => p.strandId).filter(Boolean) as string[])
  const seenGeometry = new Set(next.map((p) => canonicalGeometry(p)))
  const adopted: RoofProposal[] = []
  for (const l of doc.layers) {
    if (!isStrand(l) || !needsConfirmation(l.kind)) continue
    if (claimedNow.has(l.id) || drop.has(l.id)) continue
    const p = makeProposal({
      id: `orphan:${l.id}`,
      points: l.points,
      origin: 'legacy_unknown',
      createdAt: doc.createdAt || new Date(0).toISOString(),
    })
    drop.add(l.id)
    // A corrupt file that lost only the reference should not come back with
    // the same line drawn twice.
    if (seenGeometry.has(canonicalGeometry(p))) continue
    adopted.push(p)
  }
  const merged = adopted.length ? [...next, ...adopted] : next

  // A migration review is over when there is no unreviewed legacy run left,
  // and it is over one run at a time. Nothing bulk-approves these.
  const stillUnreviewed = merged.some((p) => p.origin === 'legacy_unknown' && p.status === 'proposed')
  const migration =
    doc.migration && doc.migration.requiresReview !== stillUnreviewed
      ? { ...doc.migration, requiresReview: stillUnreviewed }
      : doc.migration

  if (!withdrawn && !drop.size && migration === doc.migration) return { doc, withdrawn: 0 }
  return {
    doc: bump(
      doc,
      { proposals: merged, layers: doc.layers.filter((l) => !drop.has(l.id)), migration },
      { confirmationsWithdrawnByEdit: withdrawn },
    ),
    withdrawn,
  }
}

/** Which parts of a proposal are geometry or topology, for the revision bump. */
const GEOMETRY_KEYS = ['points', 'ends', 'spans', 'kind'] as const

/**
 * Edit a proposal and settle the consequences in one step.
 *
 * Callers never patch doc.proposals by hand. This is the only door, so the
 * revision bump and the invalidation cannot be forgotten on a new code path -
 * which is the same reason proposals are a separate collection rather than a
 * flag. The bump happens because an EDIT happened, not because the numbers
 * came out different: dragging a circle one ten-millionth of the photo and
 * putting it back where it started still withdraws the confirmation.
 */
export function editProposal(
  doc: LightsOsDocument,
  proposalId: string,
  patch: Partial<RoofProposal> | ((p: RoofProposal) => Partial<RoofProposal>),
): { doc: LightsOsDocument; withdrawn: number } {
  const proposals = (doc.proposals ?? []).map((p) => {
    if (p.id !== proposalId) return p
    const delta = typeof patch === 'function' ? patch(p) : patch
    const merged = { ...p, ...delta }
    if (GEOMETRY_KEYS.some((k) => k in delta)) merged.geometryRev = p.geometryRev + 1
    // Points and spans move together or the run is malformed.
    if (delta.points && !delta.spans && merged.spans.length !== merged.points.length - 1) {
      merged.spans = clearSpans(merged.points.length)
    }
    return merged
  })
  return reconcile({ ...doc, proposals })
}

/** Remove a run entirely: the proposal and any Strand it owned. */
export function removeProposal(doc: LightsOsDocument, proposalId: string): LightsOsDocument {
  const p = (doc.proposals ?? []).find((q) => q.id === proposalId)
  if (!p) return doc
  return reconcile(
    bump(
      doc,
      {
        proposals: doc.proposals.filter((q) => q.id !== proposalId),
        layers: p.strandId ? doc.layers.filter((l) => l.id !== p.strandId) : doc.layers,
      },
      { runsRejected: 1 },
    ),
  ).doc
}

/* -------------------------------------------------------------- the boundary */

export type ViolationCode =
  | 'dangling_strand_ref'
  | 'duplicate_strand_ref'
  | 'strand_ref_not_a_strand'
  | 'strand_ref_wrong_kind'
  | 'unowned_roof_strand'
  | 'orphan_priced_strand'
  | 'stale_confirmation'
  | 'geometry_mismatch'
  | 'span_shape'
  | 'needs_migration_review'

export interface Violation {
  code: ViolationCode
  message: string
  proposalId?: string
  layerId?: string
}

/**
 * WHAT MAY BE PRICED.
 *
 * Fail closed, every time. A layer reaches this list only if nothing claims it
 * at all, or if exactly one proposal claims it and every condition in
 * confirmationHolds is met. Anything else - a dangling reference, a reference
 * to a wreath, a reference to a bush, two proposals naming one Strand, a
 * hand-edited file, a roof Strand no proposal owns - is excluded, which costs
 * a quote some footage and can never invent any.
 *
 * buildQuote and buildMaterials both run every layer through this before
 * reading anything, so the two can never disagree about what was confirmed.
 */
export function quotableLayers(doc: LightsOsDocument): Layer[] {
  const blocked = blockedLayerIds(doc)
  return doc.layers.filter((l) => !blocked.has(l.id))
}

export function blockedLayerIds(doc: LightsOsDocument): Set<string> {
  const proposals = doc.proposals ?? []
  const blocked = new Set<string>()
  const claimed = new Set<string>()

  for (const p of proposals) {
    if (!p.strandId) continue
    claimed.add(p.strandId)
    if (!confirmationHolds(doc, p)) blocked.add(p.strandId)
  }

  // A roof Strand nobody claims cannot arrive through any legal path in
  // schema 4. If one is there, the file was edited or a migration was skipped,
  // and the safe reading is that no person ever agreed to it.
  for (const l of doc.layers) {
    if (!isStrand(l) || !needsConfirmation(l.kind)) continue
    if (!claimed.has(l.id)) blocked.add(l.id)
  }
  return blocked
}

/** Everything wrong with this document, in plain English, for the UI and tests. */
export function documentIntegrity(doc: LightsOsDocument): { ok: boolean; violations: Violation[] } {
  const proposals = doc.proposals ?? []
  const v: Violation[] = []
  const seen = new Map<string, number>()

  for (const p of proposals) {
    if (p.spans.length !== Math.max(0, p.points.length - 1)) {
      v.push({ code: 'span_shape', proposalId: p.id, message: 'Visibility spans do not match points.' })
    }
    if (!p.strandId) continue
    seen.set(p.strandId, (seen.get(p.strandId) ?? 0) + 1)

    const layer = doc.layers.find((l) => l.id === p.strandId)
    if (!layer) {
      v.push({
        code: 'dangling_strand_ref',
        proposalId: p.id,
        layerId: p.strandId,
        message: 'A confirmed run points at a priced item that is not in the document.',
      })
      continue
    }
    if (p.status !== 'confirmed') {
      v.push({
        code: 'orphan_priced_strand',
        proposalId: p.id,
        layerId: p.strandId,
        message: 'A run that is not confirmed still owns a priced item.',
      })
      continue
    }
    if (!isStrand(layer)) {
      v.push({
        code: 'strand_ref_not_a_strand',
        proposalId: p.id,
        layerId: p.strandId,
        message: 'A confirmed roof run points at something that is not a run at all.',
      })
      continue
    }
    if (!needsConfirmation(layer.kind)) {
      v.push({
        code: 'strand_ref_wrong_kind',
        proposalId: p.id,
        layerId: p.strandId,
        message: `A confirmed roof run points at a ${layer.kind} run.`,
      })
      continue
    }
    if (p.confirmedRev !== p.geometryRev || p.confirmedGeometry !== canonicalGeometry(p)) {
      v.push({
        code: 'stale_confirmation',
        proposalId: p.id,
        layerId: p.strandId,
        message: 'This run changed after it was confirmed.',
      })
    }
    if (!samePoints(layer.points, p.points)) {
      v.push({
        code: 'geometry_mismatch',
        proposalId: p.id,
        layerId: p.strandId,
        message: 'The priced item and the confirmed run describe different lines.',
      })
    }
  }

  for (const [sid, n] of seen) {
    if (n > 1) {
      v.push({
        code: 'duplicate_strand_ref',
        layerId: sid,
        message: 'Two runs claim the same priced item.',
      })
    }
  }

  const claimed = new Set(proposals.map((p) => p.strandId).filter(Boolean) as string[])
  for (const l of doc.layers) {
    if (!isStrand(l) || !needsConfirmation(l.kind)) continue
    if (!claimed.has(l.id)) {
      v.push({
        code: 'unowned_roof_strand',
        layerId: l.id,
        message: 'A priced roof run has no confirmation behind it.',
      })
    }
  }

  if (doc.migration?.requiresReview) {
    v.push({
      code: 'needs_migration_review',
      message: 'Roof runs from an older save have not been reviewed yet.',
    })
  }

  return { ok: v.length === 0, violations: v }
}

/* -------------------------------------------------- proposed, not counted */

export interface QuoteExclusions {
  /** Runs on screen that no quote will count. */
  proposedRuns: number
  undecidedSideReturns: number
  unresolvedOcclusions: number
  excludedGapsAwaitingSplit: number
  /** Priced items that exist but are refused by the boundary. */
  blockedStrands: number
  danglingRefs: number
  legacyRunsToReview: number
  needsReview: boolean
  /** True when anything at all is being held back. */
  any: boolean
}

export function quoteExclusions(doc: LightsOsDocument): QuoteExclusions {
  const proposals = (doc.proposals ?? []).filter((p) => p.status !== 'rejected')
  let undecidedSideReturns = 0
  let unresolvedOcclusions = 0
  let excludedGapsAwaitingSplit = 0

  /*
   * Counted across EVERY live proposal, not only the unconfirmed ones.
   *
   * Today canConfirm makes an open question and a confirmation mutually
   * exclusive, so scanning the confirmed ones finds nothing. That is a fact
   * about the current rules, not a property of the data, and a summary that
   * silently depends on it would start under-reporting the day the rules
   * change. It costs one pass.
   */
  for (const p of proposals) {
    for (const which of ['start', 'end'] as const) {
      const e = p.ends[which]
      if (e.sideReturnWarning && e.sideReturnDecision !== 'trim' && e.sideReturnDecision !== 'include') {
        undecidedSideReturns++
      }
    }
    for (const s of p.spans) {
      if (s.visibility === 'clear') continue
      if (s.decision === 'exclude') excludedGapsAwaitingSplit++
      // Only a stretch nobody can read counts as unresolved. Plain foliage in
      // front of a visible edge is not a question, so it is not a hold-up.
      else if (s.visibility === 'cannot_determine' && s.decision !== 'continues') unresolvedOcclusions++
    }
  }

  const integrity = documentIntegrity(doc)
  const blocked = blockedLayerIds(doc)
  const legacyRunsToReview = proposals.filter(
    (p) => p.origin === 'legacy_unknown' && p.status !== 'confirmed',
  ).length

  const out: QuoteExclusions = {
    proposedRuns: proposals.filter((p) => p.status !== 'confirmed').length,
    undecidedSideReturns,
    unresolvedOcclusions,
    excludedGapsAwaitingSplit,
    blockedStrands: blocked.size,
    danglingRefs: integrity.violations.filter((x) => x.code === 'dangling_strand_ref').length,
    legacyRunsToReview,
    needsReview: doc.migration?.requiresReview === true || legacyRunsToReview > 0,
    any: false,
  }
  // Every category, named explicitly. Not "proposedRuns covers it in practice".
  out.any =
    out.proposedRuns > 0 ||
    out.undecidedSideReturns > 0 ||
    out.unresolvedOcclusions > 0 ||
    out.excludedGapsAwaitingSplit > 0 ||
    out.blockedStrands > 0 ||
    out.danglingRefs > 0 ||
    out.legacyRunsToReview > 0 ||
    out.needsReview
  return out
}

/** The migrated runs still waiting on a person, in document order. */
export function legacyRunsAwaitingReview(doc: LightsOsDocument): RoofProposal[] {
  return (doc.proposals ?? []).filter((p) => p.origin === 'legacy_unknown' && p.status === 'proposed')
}

/* ------------------------------------------------------------------ counting */

/** Fold a correction count into the log without making the log load bearing. */
function bump(
  doc: LightsOsDocument,
  patch: Partial<LightsOsDocument>,
  counts?: Partial<CorrectionCounts>,
): LightsOsDocument {
  const next = { ...doc, ...patch }
  if (!counts) return next
  const log = doc.corrections ?? emptyCorrectionLog()
  const merged = { ...log.counts }
  for (const k of Object.keys(counts) as (keyof CorrectionCounts)[]) {
    merged[k] = (merged[k] ?? 0) + (counts[k] ?? 0)
  }
  next.corrections = { ...log, counts: merged }
  return next
}

export function countCorrection(
  doc: LightsOsDocument,
  counts: Partial<CorrectionCounts>,
): LightsOsDocument {
  return bump(doc, {}, counts)
}

/* ----------------------------------------------------------------- migration */

/**
 * SCHEMA 3 TO 4.
 *
 * The honest reading of schema 3 is that it recorded no provenance at all.
 * There is no field, and no side channel, that separates a run a person drew
 * with a finger from a run the automatic tracer laid down after a sweep. So
 * the manual lane is defined on an explicit marker - strand.origin, which
 * schema 4 writes - and no schema 3 document has one. Every legacy roof run
 * therefore migrates as proposed.
 *
 * There is no bulk approval on the other side of this. The runs come back one
 * at a time, each with its own Confirm, because "I drew all forty of these
 * myself" is exactly the blanket acceptance this layer exists to stop, and a
 * contractor tapping it on a job he half remembers is how unreviewed geometry
 * gets priced.
 *
 * Only roof runs are touched. Bushes, wraps, garland and wreaths carry on
 * being priced, because none of them are roof edges and none of them are what
 * a detector got wrong.
 */
export function migrateV3toV4(
  doc: LightsOsDocument,
  opts: { now: string; makeId: () => string },
): LightsOsDocument {
  const proposals: RoofProposal[] = [...(doc.proposals ?? [])]
  const layers: Layer[] = []
  let migrated = 0

  for (const l of doc.layers) {
    if (!isStrand(l) || !needsConfirmation(l.kind)) {
      layers.push(l)
      continue
    }
    const marked = l.origin === 'manual' || l.origin === 'confirmed'
    const p = makeProposal({
      id: opts.makeId(),
      points: l.points,
      origin: marked ? 'manual' : 'legacy_unknown',
      createdAt: doc.createdAt || opts.now,
    })
    if (marked) {
      // The marker says a person drew this and approved it. Schema 3 never
      // wrote one, so this branch is for schema 4 documents arriving from a
      // future migration, not for anything on disk today.
      proposals.push({
        ...p,
        status: 'confirmed',
        confirmedAt: opts.now,
        strandId: l.id,
        confirmedRev: p.geometryRev,
        confirmedGeometry: canonicalGeometry(p),
        confirmedFingerprint: geometryFingerprint(p),
      })
      layers.push(l)
    } else {
      proposals.push(p)
      migrated++
    }
  }

  const next: LightsOsDocument = {
    ...doc,
    proposals,
    layers,
    corrections: doc.corrections ?? null,
    migration: {
      fromVersion: doc.schemaVersion ?? 3,
      at: opts.now,
      legacyRoofRuns: migrated,
      requiresReview: migrated > 0,
    },
  }
  // The sweep also proves the migrated document is internally consistent
  // before anybody sees it.
  return reconcile(next).doc
}

/**
 * THE ONE DOOR EVERY SAVED JOB COMES BACK THROUGH.
 *
 * There are three of them - IndexedDB, the old localStorage store, and a
 * restored backup file - and a roof run that skipped this on any one of them
 * would arrive in the priced collection with nobody behind it. The boundary
 * would refuse it, which is the right failure, but the contractor would see a
 * job he quoted last season come back with its roofline missing and no
 * explanation. This is the explanation: it turns into a run he can review.
 */
export function upgradeDocument(
  doc: LightsOsDocument,
  opts: { now: string; makeId: () => string },
): LightsOsDocument {
  const from = typeof doc.schemaVersion === 'number' ? doc.schemaVersion : 3
  const d: LightsOsDocument = {
    ...doc,
    proposals: Array.isArray(doc.proposals) ? doc.proposals : [],
    corrections: doc.corrections ?? null,
    /*
     * The paper is brought up to shape on the way in, every time. A document
     * saved before deposits could be none, before the balance had its own
     * timing and before payments were a list is missing three fields, and a
     * missing field on this record is a NaN on somebody's invoice.
     */
    paper: normalizePaper(doc.paper, doc.quote ?? []),
    /*
     * Schema 5. A job written before sections existed opens with an empty
     * list and is otherwise untouched: no run is reassigned, no measurement is
     * invented, and nothing about what it is worth changes.
     */
    sections: Array.isArray(doc.sections) ? doc.sections : [],
    approvals: Array.isArray(doc.approvals) ? doc.approvals : [],
    /* Every job saved before the calendar existed opens with an empty one. */
    visits: Array.isArray(doc.visits) ? doc.visits : [],
    /*
     * A JOB WITH NO PRICING BLOCK IS A WHITE SCREEN, NOT A FREE JOB.
     *
     * Nothing in the app wrote a document without one, so every reader assumed
     * it. Then a screen read doc.pricing.rates and a record written by an
     * older client, or by hand, took the whole app down rather than showing
     * one missing number. The rest of this function is defensive about every
     * other collection for exactly this reason; pricing was the one that had
     * never been asked to be.
     */
    pricing: {
      ...defaultPricing(),
      ...(doc.pricing ?? {}),
      rates: { ...defaultRates(), ...(doc.pricing?.rates ?? {}) },
      overrides: doc.pricing?.overrides ?? {},
      extras: Array.isArray(doc.pricing?.extras) ? doc.pricing.extras : [],
    },
    // Older jobs were measured with the perspective correction always on.
    // They come back with it off, like everything else, and say estimate.
    takeoff: {
      ...doc.takeoff,
      depthCorrection: doc.takeoff?.depthCorrection === true,
    },
  }
  if (from >= 4) return { ...reconcile(d).doc, schemaVersion: SCHEMA_VERSION }
  return { ...migrateV3toV4(d, opts), schemaVersion: SCHEMA_VERSION }
}

/* ------------------------------------------------------ names people read */

/**
 * "Roof run 3".
 *
 * Every card used to be titled "Roof edge", which is a category, not a name.
 * With three of them on a house neither a contractor nor a test could tell
 * which one was on screen, and a mistap left the previous run's card open with
 * nothing to give it away. The number is assigned once and never reused, so
 * deleting run 1 does not renumber the run somebody is looking at.
 */
export const runLabel = (p: RoofProposal, index = 0) => `Roof run ${p.seq ?? index + 1}`

/** The kind, for the line underneath the name. */
export const runTypeLabel = (p: RoofProposal) => (p.kind === 'rake' ? 'Gable rake' : 'Roof edge')

/** The next unused number on this document. */
export const nextSeq = (doc: LightsOsDocument) =>
  (doc.proposals ?? []).reduce((n, p) => Math.max(n, p.seq ?? 0), 0) + 1

/**
 * WHAT THE ORDINARY VIEW SAYS WHEN RUNS ARE WAITING.
 *
 * It used to say "Nothing drawn yet" with three dashed runs on the
 * photograph, because nothing was in the design yet - true of the design, and
 * a plain lie about the screen.
 */
export function proposedSummary(doc: LightsOsDocument): string | null {
  const n = (doc.proposals ?? []).filter((p) => p.status === 'proposed').length
  if (!n) return null
  return `${n} suggested run${n === 1 ? '' : 's'} - review to include in your design.`
}
